import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { __ADS_INTERNAL__ } from '@/lib/ads/shouldShowAds';

/**
 * Section C5 — emit `<meta name="robots" content="noindex,nofollow">` on every
 * non-publisher route (auth, account, admin, billing, forms, exam practice,
 * parent dashboard, etc.). Reuses the AdSense deny list as a single source of
 * truth so the SEO surface and AdSense surface stay aligned.
 *
 * Public, publisher-content routes are left untouched: their own pages /
 * EnLayout / Helmet blocks emit `index,follow` and a canonical URL.
 */
const EXTRA_NOINDEX_PREFIXES = [
  '/request',
  '/requests',
  // Legacy sections kept in the codebase but no longer part of the marketplace
  // product: exam prep, community talk, deals, fuel prices. Data untouched.
  '/exam',
  '/talk',
  '/uram',
  '/fuel',
  '/exam/practice',

  // Retired directory-product content (food / café / karaoke / taxi / parking /
  // health guides, editorial desk, place-ranking explainers). Kept reachable so
  // inbound links don't 404, but excluded from the sitemap and from search.
  '/guides',
  '/editorial',
  '/editorial-policy',
  '/correction-policy',
  '/fact-checking-policy',
  '/en/how-we-rank-places',
  '/en/report-corrections',
  '/en/ulaanbaatar-food-guide',
  '/en/ulaanbaatar-student-guide',
  '/en/budget-food-in-ulaanbaatar',
  '/en/community-guidelines',


  '/parent/dashboard',
  '/merchant',
  '/value/submit',
  '/value/saved',
  '/value/community-board',
  '/search',
  // Private / account-scoped Trust routes only — public entity pages under
  // /trust/... are publisher content and stay indexable (they are in sitemap.xml).
  '/trust/claim',
  '/trust/business',
  '/trust/reports',
  '/trust/analytics',
  '/trust/household',
  '/trust/review',
  // Composer routes for community sections; the hubs themselves are indexable.
  '/talk/new',
  '/talk/compose',
  '/uram/deals/new',
  '/share-location',
  '/l',
];

function isNoIndexPath(path: string): boolean {
  const p = path.toLowerCase().split('?')[0].split('#')[0];
  // Public provider profiles and join page remain indexable; dashboard is private.
  if (p === '/provider' || p === '/provider/') return true;
  for (const prefix of __ADS_INTERNAL__.DENY_PREFIXES) {
    if (p === prefix || p.startsWith(prefix + '/')) return true;
  }
  for (const prefix of EXTRA_NOINDEX_PREFIXES) {
    if (p === prefix || p.startsWith(prefix + '/')) return true;
  }
  return false;
}

export function RouteNoIndex() {
  const { pathname } = useLocation();
  if (!isNoIndexPath(pathname)) return null;
  return (
    <Helmet>
      <meta name="robots" content="noindex,nofollow" />
      <meta name="googlebot" content="noindex,nofollow" />
    </Helmet>
  );
}

export const __ROUTE_NOINDEX_INTERNAL__ = { isNoIndexPath, EXTRA_NOINDEX_PREFIXES };